import {useEffect, useMemo, useState} from 'react'
import {useQuery, useQueries} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {ArrowLeft, ArrowRight, ChevronRight, Layers, User} from 'lucide-react'
import {useNavigate, useLocation} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {ErrorText} from '@/components/shared/ErrorText'
import {ExpirationDateRow} from '@/components/shared/ExpirationDateRow'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {ErrorBanner} from '@/components/ui/error-banner'
import {FunctionGroupRequestCreateRequestFields} from '@/types/function-group'
import type {FunctionGroup, FunctionGroupRequestCreateRequest, FunctionGroupRequestItemCreate, FunctionGroupAccessProfileList} from '@/types/function-group'
import {formatDateOnly, isValidExpirationDate} from '@/lib/date-utils'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {permissions} from '@/lib/permissions'
import {MutedText} from '@/components/shared/MutedText'
import {PageLayout} from '@/components/shared/PageLayout'
import {InlineRow} from '@/components/shared/InlineRow'
import {ExpandedContent} from '@/components/shared/ExpandedContent'
import {Icon} from '@/components/shared/Icon'
import {LookupCombobox} from '@/components/shared/LookupCombobox'
import {useStepFocus} from '@/hooks/use-step-focus'
import type {LookupDescriptor} from '@/types/endpoint-contract'
import {AssociationEditor} from '@/components/shared/AssociationEditor'
import type {AssociationColumn} from '@/components/shared/AssociationEditor'
import {HoldingStatusBadge} from '@/components/shared/HoldingStatusBadge'
import {useFunctionGroupHoldings} from '@/hooks/useHoldingStatuses'
import {TextLink} from '@/components/shared/TextLink'
import {CONCEPT_ICON} from '@/lib/concepts'

const IDENTITY_LOOKUP: LookupDescriptor = {
     
    endpoint: 'apiInternalIdentities',
     
    labelField: 'partyDisplayName',
     
    searchParam: 'q',
}

const WizardStep = {
    SELECT_BENEFICIARY: 0,
    SELECT_FUNCTION_GROUPS: 1,
    REVIEW: 2,
} as const

interface WizardItem {
    functionGroup: FunctionGroup
    expiresAt: string
}

export function FunctionGroupRequestWizard() {
    const services = useServices()
    const i18n = useI18n()
    const {functionGroupTypes, environmentTypes} = useAppConfig()
    usePageTitle(i18n.functionGroupRequests.newPageTitle)
    const navigate = useNavigate()
    const location = useLocation()
    const {user, hasPermission} = useAuth()
    const canViewFgAccessProfiles = hasPermission(permissions.functionGroups.view)

    const STEPS = [
        {id: 'select-beneficiary', title: i18n.functionGroupRequests.beneficiary as string},
        {id: 'select', title: i18n.functionGroupRequests.wizardSelectFunctionGroups as string},
        {id: 'review', title: i18n.functionGroupRequests.wizardReviewSubmit as string},
    ]

    const isMyRoute = location.pathname.startsWith(`${routes.my.root()}/`)
    const backPath = isMyRoute ? routes.my.functionGroupRequests.list() : routes.accessGovernance.functionGroupRequests.list()

    const [step, setStep] = useState(0)
    const stepContainerRef = useStepFocus(step, STEPS[step].title)
    const [beneficiaryId, setBeneficiaryId] = useState(user?.identityId || '')
    const [beneficiaryName, setBeneficiaryName] = useState('')
    const [isSelfRequest, setIsSelfRequest] = useState(true)
    const [fgSearch, setFgSearch] = useState('')
    const [selectedItems, setSelectedItems] = useState<WizardItem[]>([])
    const [justification, setJustification] = useState('')
    const [expandedFgIds, setExpandedFgIds] = useState<Set<string>>(new Set())

    const toggleFgExpansion = (fgId: string) => {
        setExpandedFgIds(prev => {
            const next = new Set(prev)
            if (next.has(fgId)) next.delete(fgId)
            else next.add(fgId)
            return next
        })
    }

    const selectedFunctionGroupIds = useMemo(
        () => selectedItems.map((i) => i.functionGroup.id),
        [selectedItems],
    )
    const {data: functionGroupHoldings} = useFunctionGroupHoldings(
        beneficiaryId || undefined,
        selectedFunctionGroupIds,
    )

    const fgColumns: AssociationColumn<WizardItem>[] = [
        {
            header: String(i18n.common.name),
            render: (item) => {
                const expanded = expandedFgIds.has(item.functionGroup.id)
                const apData = canViewFgAccessProfiles ? apDataByFgId.get(item.functionGroup.id) : undefined
                return (
                    <Stack gap="xs">
                        <InlineRow gap="sm" className="min-w-0">
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={(e) => { e.stopPropagation(); toggleFgExpansion(item.functionGroup.id) }}
                                aria-expanded={expanded}
                                aria-label={i18n.common.expand as string}
                                data-testid={`btn-toggle-fg-${item.functionGroup.id}`}
                            >
                                <ChevronRight
                                    className={expanded ? 'h-4 w-4 rotate-90 transition-transform' : 'h-4 w-4 transition-transform'}
                                />
                            </Button>
                            <Icon icon={Layers} muted className="shrink-0"/>
                            <Strong truncate>{item.functionGroup.name}</Strong>
                        </InlineRow>
                        {apData && apData.totalCount > 0 && (
                            <MutedText size="xs" className="pl-14" data-testid={`wizard-fg-grants-summary-${item.functionGroup.id}`}>
                                {String(i18n.requestWizardLinkedAccessProfiles.grantsCount({count: apData.totalCount}))}
                            </MutedText>
                        )}
                    </Stack>
                )
            },
        },
        {
            header: String(i18n.common.type),
            render: (item) => item.functionGroup.functionGroupType
                ? <Badge variant="outline">{labelOf(functionGroupTypes, item.functionGroup.functionGroupType)}</Badge>
                : null,
        },
        {
            header: '',
            render: (item) => (
                <HoldingStatusBadge
                    holding={functionGroupHoldings?.[item.functionGroup.id]}
                    data-testid={`holding-status-${item.functionGroup.id}`}
                />
            ),
            // eslint-disable-next-line i18next/no-literal-string -- CSS className, not user text
            className: 'w-0',
        },
        {
            header: '',
            render: (item) => item.functionGroup.expiryRequired
                ? <Badge variant="outline" className="text-xs whitespace-nowrap">{i18n.functionGroupRequests.expiryRequired}</Badge>
                : null,
            // eslint-disable-next-line i18next/no-literal-string -- CSS className, not user text
            className: 'w-0',
        },
    ]

    // Resolve the picked identity's display name for the Step 2 review summary.
    // The combobox itself shows the label inline once selected; this query is only
    // needed because the review step renders the name outside the picker.
    const beneficiaryDetailQuery = useQuery({
        queryKey: ['identity-detail', beneficiaryId],
        queryFn: () => services.identities.detail(beneficiaryId),
        enabled: !isSelfRequest && !!beneficiaryId,
    })
    useEffect(() => {
        if (!isSelfRequest && beneficiaryDetailQuery.data) {
            setBeneficiaryName(beneficiaryDetailQuery.data.party.displayName)
        }
    }, [isSelfRequest, beneficiaryDetailQuery.data])

    // Function group search (requestable only)
    const {data: fgResults} = useQuery({
        queryKey: ['fg-search', fgSearch],
        queryFn: () => services.functionGroups.list({q: fgSearch, size: 50, requestable: true}),
        enabled: step === 1,
    })

    const createMutation = useEntityMutation({
        mutationFn: (req: FunctionGroupRequestCreateRequest) =>
            services.functionGroupRequests.create(req),
        invalidate: ['my-fg-requests'],
        successMessage: i18n.functionGroupRequests.requestSubmitted,
        onSuccess: (data) => {
            if (isMyRoute) {
                navigate(routes.my.functionGroupRequests.detail(data.id))
            } else {
                navigate(routes.accessGovernance.functionGroupRequests.detail(data.id))
            }
        },
    })

    // Fetch linked access profiles for each selected FG. Displayed below each
    // selected FG row in Step 2 and again in the Step 3 review summary. Skipped
    // when the user lacks function-groups:view permission — the block simply
    // won't render.
    const fgApQueries = useQueries({
        queries: canViewFgAccessProfiles
            ? selectedItems.map(item => ({
                queryKey: ['fg-ap-wizard', item.functionGroup.id],
                queryFn: () =>
                    services.functionGroups.accessProfiles(item.functionGroup.id),
            }))
            : [],
    })
    const apDataByFgId = new Map<string, FunctionGroupAccessProfileList>()
    selectedItems.forEach((item, idx) => {
        const data = fgApQueries[idx]?.data
        if (data) apDataByFgId.set(item.functionGroup.id, data)
    })

    const addFunctionGroup = (fg: FunctionGroup) => {
        if (selectedItems.some(i => i.functionGroup.id === fg.id)) return
        setSelectedItems([...selectedItems, {
            functionGroup: fg,
            expiresAt: '',
        }])
        // Auto-expand rows that have a required input so the user can't miss it.
        if (fg.expiryRequired) {
            setExpandedFgIds(prev => new Set(prev).add(fg.id))
        }
    }

    const expandAllFgs = () => {
        setExpandedFgIds(new Set(selectedItems.map(i => i.functionGroup.id)))
    }

    const collapseAllFgs = () => {
        setExpandedFgIds(new Set())
    }

    const allExpanded = selectedItems.length > 0 && selectedItems.every(i => expandedFgIds.has(i.functionGroup.id))
    const allCollapsed = expandedFgIds.size === 0

    // When the user enters the review step, start with everything collapsed so
    // the summary is compact. User can Expand all or toggle individual rows.
    useEffect(() => {
        if (step === WizardStep.REVIEW) {
            setExpandedFgIds(new Set())
        }
    }, [step])

    const removeFunctionGroup = (fgId: string) => {
        setSelectedItems(selectedItems.filter(i => i.functionGroup.id !== fgId))
    }

    const updateItemExpiresAt = (fgId: string, value: string) => {
        setSelectedItems(selectedItems.map(i =>
            i.functionGroup.id === fgId ? {...i, expiresAt: value} : i
        ))
    }

    // Read-only preview of access profiles linked to a function group.
    // Used below each selected FG row in Step 2 and in the Step 3 review summary.
    const renderLinkedAccessProfiles = (fgId: string) => {
        // Permission-gated: show muted "not available" message instead of silently hiding.
        if (!canViewFgAccessProfiles) {
            return (
                <MutedText size="xs" data-testid={`wizard-fg-linked-ap-not-available-${fgId}`}>
                    {i18n.requestWizardLinkedAccessProfiles.notAvailable}
                </MutedText>
            )
        }
        const apData = apDataByFgId.get(fgId)
        if (!apData) return null
        if (apData.totalCount === 0) {
            return (
                <MutedText size="xs" data-testid={`wizard-fg-linked-ap-empty-${fgId}`}>
                    {i18n.requestWizardLinkedAccessProfiles.noAccessProfiles}
                </MutedText>
            )
        }
        return (
            <div data-testid={`wizard-fg-linked-ap-${fgId}`}>
                {apData.items.slice(0, 5).map(ap => (
                    <InlineRow key={ap.id} gap="xs" className="mt-0.5">
                        <TextLink
                            to={routes.accessGovernance.profiles.detail(ap.accessProfile.id)}
                            testId={`wizard-ap-link-${ap.accessProfile.id}`}
                        >
                            {ap.accessProfile.displayName}
                        </TextLink>
                        <MutedText size="xs">{labelOf(environmentTypes, ap.environment)}</MutedText>
                        <RiskLevelBadge riskLevel={ap.riskLevel} className="text-xs"/>
                    </InlineRow>
                ))}
                {apData.totalCount > 5 && (
                    <MutedText size="xs">
                        {String(i18n.requestWizardLinkedAccessProfiles.moreText({count: apData.totalCount - 5}))}
                    </MutedText>
                )}
            </div>
        )
    }

    const canProceed = () => {
        switch (step) {
            case WizardStep.SELECT_BENEFICIARY:
                return !!beneficiaryId
            case WizardStep.SELECT_FUNCTION_GROUPS:
                return selectedItems.length > 0 && selectedItems.every(i => {
                    if (i.functionGroup.expiryRequired && !i.expiresAt) return false
                    if (!isValidExpirationDate(i.expiresAt, i.functionGroup.maxDurationDays)) return false
                    return true
                })
            case WizardStep.REVIEW:
                return justification.trim().length > 0
            default:
                return false
        }
    }

    const handleSubmit = () => {
        const items: FunctionGroupRequestItemCreate[] = selectedItems.map(i => ({
            functionGroupId: i.functionGroup.id,
            // eslint-disable-next-line i18next/no-literal-string
            expiresAt: i.expiresAt ? `${i.expiresAt}T23:59:59Z` : undefined,
        }))

        createMutation.mutate({
            beneficiaryIdentityId: beneficiaryId,
            justification,
            items,
        })
    }

    const wizardNavigation = (
        <>
            {step === 0 ? (
                <Button variant="outline" onClick={() => navigate(backPath)} data-testid="btn-cancel">
                    {i18n.common.cancel}
                </Button>
            ) : (
                <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="btn-previous-step">
                    <Icon icon={ArrowLeft} size="sm" className="mr-2"/>
                    {i18n.common.back}
                </Button>
            )}
            {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} data-testid="btn-next-step">
                    {i18n.autoAssignRules.next}
                    <Icon icon={ArrowRight} size="sm" className="ml-2"/>
                </Button>
            ) : (
                <Button onClick={handleSubmit}
                        disabled={!canProceed() || createMutation.isPending}
                        data-testid="btn-submit-request">
                    {createMutation.isPending ? i18n.functionGroupRequests.submitting : i18n.functionGroupRequests.submitRequest}
                </Button>
            )}
        </>
    )

    return (
        <PageLayout
            variant="wizard"
            icon={CONCEPT_ICON.functionGroupRequest}
            title={i18n.functionGroupRequests.wizardTitle}
            backTo={backPath}
            wizardSteps={STEPS}
            wizardCurrentStep={step}
            wizardNavigation={wizardNavigation}
            data-testid="function-group-request-wizard-page"
        >

            <div ref={stepContainerRef} data-testid="fg-request-step-container">

            {/* Step 0: Beneficiary */}
            {step === 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-who-needs-access">{i18n.functionGroupRequests.whoNeedsAccess}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <InlineRow gap="lg">
                            <Button
                                variant={isSelfRequest ? 'default' : 'outline'}
                                onClick={() => {
                                    setIsSelfRequest(true)
                                    setBeneficiaryId(user?.identityId || '')
                                    setBeneficiaryName('')
                                }}
                                data-testid="btn-select-myself">
                                <Icon icon={User} size="sm" className="mr-2"/>
                                {i18n.functionGroupRequests.myself}
                            </Button>
                            <Button
                                variant={!isSelfRequest ? 'default' : 'outline'}
                                onClick={() => {
                                    setIsSelfRequest(false)
                                    setBeneficiaryId('')
                                }}
                                data-testid="btn-select-someone-else">
                                <Icon icon={User} size="sm" className="mr-2"/>
                                {i18n.functionGroupRequests.someoneElse}
                            </Button>
                        </InlineRow>

                        {!isSelfRequest && (
                            <Stack gap="sm">
                                <Label htmlFor="beneficiary-identity">{i18n.functionGroupRequests.searchForIdentity}</Label>
                                <LookupCombobox
                                    mode="single"
                                    value={beneficiaryId
                                        ? {id: beneficiaryId, displayName: beneficiaryName || beneficiaryId, entityType: 'IDENTITY', subType: null, code: null}
                                        : null}
                                    onChange={(next) => {
                                        setBeneficiaryId(next ? next.id : '')
                                        setBeneficiaryName(next ? next.displayName : '')
                                    }}
                                    lookup={IDENTITY_LOOKUP}
                                    displayName={String(i18n.functionGroupRequests.beneficiary)}
                                    testIdPrefix="beneficiary-identity"
                                />
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Step 1: Select Function Groups */}
            {step === 1 && (
                <Card>
                    <CardHeader className="pb-2">
                        <InlineRow className="justify-between items-center">
                            <CardTitle data-testid="card-title-select-function-groups" className="text-sm">{i18n.functionGroupRequests.selectFunctionGroups}</CardTitle>
                            {selectedItems.length >= 2 && (
                                <InlineRow gap="xs">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={expandAllFgs}
                                        disabled={allExpanded}
                                        data-testid="btn-expand-all-fgs"
                                    >
                                        {i18n.functionGroupRequests.expandAll}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={collapseAllFgs}
                                        disabled={allCollapsed}
                                        data-testid="btn-collapse-all-fgs"
                                    >
                                        {i18n.functionGroupRequests.collapseAll}
                                    </Button>
                                </InlineRow>
                            )}
                        </InlineRow>
                    </CardHeader>
                    <CardContent>
                        <AssociationEditor<WizardItem>
                            items={selectedItems}
                            onSearch={setFgSearch}
                            searchResults={(fgResults?.content ?? []).map(fg => ({
                                functionGroup: fg,
                                expiresAt: '',
                            }))}
                            onAdd={(item) => addFunctionGroup(item.functionGroup)}
                            onRemove={(item) => removeFunctionGroup(item.functionGroup.id)}
                            getKey={(item) => item.functionGroup.id}
                            getLabel={(item) => item.functionGroup.name}
                            searchPlaceholder={i18n.functionGroupRequests.searchFunctionGroups as string}
                            emptyMessage={i18n.functionGroupRequests.noFunctionGroupsSelected as string}
                            columns={fgColumns}
                            testId="fg-association-editor"
                            onRowClick={(item) => toggleFgExpansion(item.functionGroup.id)}
                            renderItemExtra={(item) => {
                                // Collapsed by default after initial auto-expand on add.
                                if (!expandedFgIds.has(item.functionGroup.id)) return null
                                return (
                                    // Align the collapsible content with the chevron arrow in
                                    // the row above. pt-2 gives breathing room.
                                    <Stack gap="sm" className="pl-1 pt-2">
                                        <ExpirationDateRow
                                            value={item.expiresAt}
                                            onChange={v => updateItemExpiresAt(item.functionGroup.id, v)}
                                            required={item.functionGroup.expiryRequired}
                                            maxDurationDays={item.functionGroup.maxDurationDays}
                                            label={String(i18n.functionGroupRequests.expirationDate)}
                                            maxDurationHint={item.functionGroup.maxDurationDays ? String(i18n.functionGroupRequests.maxDurationHint({days: item.functionGroup.maxDurationDays})) : undefined}
                                            testId={`expiration-date-${item.functionGroup.id}`}
                                        />
                                        {renderLinkedAccessProfiles(item.functionGroup.id)}
                                    </Stack>
                                )
                            }}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Step 2: Review & Submit */}
            {step === 2 && (
                <Stack>
                    <Card>
                        <CardHeader>
                            <InlineRow className="justify-between items-center">
                                <CardTitle data-testid="card-title-review-and-submit">{i18n.functionGroupRequests.reviewAndSubmit}</CardTitle>
                                {selectedItems.length >= 2 && (
                                    <InlineRow gap="xs">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={expandAllFgs}
                                            disabled={allExpanded}
                                            data-testid="btn-expand-all-review"
                                        >
                                            {i18n.functionGroupRequests.expandAll}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={collapseAllFgs}
                                            disabled={allCollapsed}
                                            data-testid="btn-collapse-all-review"
                                        >
                                            {i18n.functionGroupRequests.collapseAll}
                                        </Button>
                                    </InlineRow>
                                )}
                            </InlineRow>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <MutedText className="block">{i18n.functionGroupRequests.reviewBeneficiary}</MutedText>
                                <Strong>
                                    {isSelfRequest ? (user?.name || i18n.functionGroupRequests.myself) : beneficiaryName}
                                </Strong>
                            </div>
                            <div>
                                <MutedText className="block mb-2">{i18n.functionGroupRequests.reviewFunctionGroups({count: selectedItems.length})}</MutedText>
                                <Stack gap="md">
                                {selectedItems.map(item => {
                                    const expanded = expandedFgIds.has(item.functionGroup.id)
                                    return (
                                        <div key={item.functionGroup.id} data-testid="review-fg-item">
                                            <InlineRow className="min-w-0">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    onClick={() => toggleFgExpansion(item.functionGroup.id)}
                                                    aria-expanded={expanded}
                                                    aria-label={i18n.common.expand as string}
                                                    data-testid={`btn-toggle-review-fg-${item.functionGroup.id}`}
                                                >
                                                    <ChevronRight
                                                        className={expanded ? 'h-4 w-4 rotate-90 transition-transform' : 'h-4 w-4 transition-transform'}
                                                    />
                                                </Button>
                                                <Icon icon={Layers} size="sm" muted className="shrink-0"/>
                                                <Strong truncate>{item.functionGroup.name}</Strong>
                                                {item.expiresAt && (
                                                    <MutedText>
                                                        {i18n.functionGroupRequests.untilDate({date: formatDateOnly(item.expiresAt)})}
                                                    </MutedText>
                                                )}
                                            </InlineRow>
                                            {expanded && (
                                                <ExpandedContent>
                                                    {renderLinkedAccessProfiles(item.functionGroup.id)}
                                                </ExpandedContent>
                                            )}
                                        </div>
                                    )
                                })}
                                </Stack>
                            </div>
                            <div>
                                <Label htmlFor={FunctionGroupRequestCreateRequestFields.justification}>
                                    {i18n.functionGroupRequests.justification} <ErrorText>*</ErrorText>
                                </Label>
                                <Textarea id={FunctionGroupRequestCreateRequestFields.justification} value={justification}
                                          onChange={e => setJustification(e.target.value)}
                                          placeholder={i18n.functionGroupRequests.justificationPlaceholder}
                                          rows={4}/>
                            </div>
                        </CardContent>
                    </Card>

                    <ErrorBanner error={createMutation.error}/>
                </Stack>
            )}

            </div>
        </PageLayout>
    )
}
